import { Info, Copy, Check, Plus, Trash2, X, CopyCheck } from "lucide-react";
import moment from "moment";
import { useEffect, useMemo, useRef, useState } from "react";
import Style from "./style.module.css";
import React from "react";
import { useApiCall } from "../custom-hooks/api-call";
import AvatarProfile from "../ui/profile";
import Tooltip from "../ui/tooltip";
import Swal from "sweetalert2";
import { useClickOutside } from "../custom-hooks/click-outside";

export function FieldRow({
  label,
  value,
  type = "text",
  opt = [],
  keyName,
  isLock = false,
  unApprovedVal,
  LabelIcon,
  isLink = false,
  LinkValue,
  isCapitalize = true,
  isCopyable = false, // ✅ new prop
}) {
  const [isActive, setIsActive] = useState(false);
  const [fieldVal, setFieldVal] = useState(value || "");
  const [copied, setCopied] = useState(false); // ✅ track copied state

  // ❌ hide empty or placeholder values
  if (
    value === "-" ||
    value === "--" ||
    !value ||
    value === null ||
    value === undefined
  ) {
    return null;
  }

  // 📋 copy to clipboard with feedback
  const handleCopy = () => {
    navigator.clipboard.writeText(value);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000); // 2 sec feedback
  };

  return (
    <div className={`${Style.fieldRow} flex flex-col gap-[18px] w-full py-3`}>
      <div className="flex items-center gap-1 text-[14px] font-medium text-[#0E121B]">
        <LabelIcon size={"1em"} />
        <span>{label}</span>
      </div>

      <div className="w-full relative flex items-center">
        {/* Link Field */}
        {!isActive && isLink ? (
          <a
            href={LinkValue || value}
            target="_blank"
            rel="noopener noreferrer"
            className={`${isCapitalize ? "capitalize" : ""} text-[14px] text-blue-600 border-b-[1px] pb-[18px] border-[#CACFD8] block w-full`}
          >
            {fieldVal}
          </a>
        ) : (
          <input
            type="text"
            className={`${isCapitalize ? "capitalize" : ""} text-[14px] w-full font-normal text-[#999] ${isActive ? "border-[1px] p-[10px] rounded-md" : "border-b-[1px] pb-[15px]"} border-solid border-[#CACFD8] ${!isActive ? "cursor-default" : ""}`}
            value={fieldVal}
            onChange={(e) => setFieldVal(e.target.value)}
            readOnly={!isActive}
          />
        )}

        {/* Copy Icon / Copied Feedback */}
        {isCopyable && !isActive && (
          <span
            onClick={handleCopy}
            className="absolute right-0 top-1/2 -translate-y-1/2 flex items-center gap-1 cursor-pointer text-gray-400 hover:text-gray-600"
            title="Copy"
          >
            {copied ? (
              <>
                <Check size={16} className="text-green-500" />
                <span className="text-[12px] text-green-500">Copied</span>
              </>
            ) : (
              <Copy size={16} />
            )}
          </span>
        )}
      </div>
    </div>
  );
}

export function DynamicTable({
  data = [],
  columns = [],
  rowKey = "id",
  statusColor = {},
  onRowClick, // optional fallback
  rowUrlBuilder, // function: (row) => string
}) {
  const [copiedCell, setCopiedCell] = useState(null);
  if (!data || data.length === 0) return <div>No data available</div>;

  const handleCopy = (value, key) => {
    navigator.clipboard.writeText(value);

    setCopiedCell(key);

    setTimeout(() => {
      setCopiedCell(null);
    }, 2000); // 2 seconds
  };

  return (
    <div className="overflow-x-auto w-full">
      <table className="w-full border-collapse text-sm">
        <thead className="bg-gray-100 text-gray-700 font-semibold">
          <tr>
            {columns.map((col,i) => (
              <th key={col.key} className="py-2 px-4">
                <div className="flex items-center gap-[2px]">
                  {col.icon && (
                    <col.icon size={"1em"} className="text-gray-500" />
                  )}
                  <span>{col.label}</span>
                </div>
              </th>
            ))}
          </tr>
        </thead>

        <tbody>
          {data?.map((row, idx) => (
            <tr
              key={row[rowKey] ?? idx}
              className={`cursor-pointer transition-all duration-200 ${idx % 2 === 0 ? "bg-white text-black" : "bg-[#f8fafc] shadow-card text-slate-500"}`}
              onClick={() => {
                if (rowUrlBuilder) {
                  // generate URL dynamically for this row
                  const url = rowUrlBuilder(row);
                  window.open(url, "_blank"); // new tab
                } else {
                  onRowClick?.(row); // fallback
                }
              }}
            >
              {columns.map((col) => (
                // <td key={col.key} className="py-2 px-4">
                //     {col.render ? col.render(row) : row[col.key] ?? "-"}
                // </td>
                <td key={col.key} className="py-2 px-4">
                  {col.copy ? (
                    <div
                      className="flex items-center gap-1 justify-between"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <span title={row[col.key]}>
                        {row[col.key]
                          ? row[col.key].length > 15
                            ? row[col.key].slice(0, 15) + "..."
                            : row[col.key]
                          : "-"}
                      </span>

                      {row[col.key] && (
                        <button
                          onClick={() =>
                            handleCopy(
                              row[col.key],
                              `${row[rowKey]}-${col.key}-${idx}`,
                            )
                          }
                          className="text-gray-400 hover:text-gray-600"
                        >
                          {copiedCell === `${row[rowKey]}-${col.key}-${idx}` ? (
                            <CopyCheck size={14}  />
                          ) : (
                            <Copy size={14} />
                          )}
                        </button>
                      )}
                    </div>
                  ) : col.render ? (
                    col.render(row)
                  ) : (
                    (row[col.key] ?? "-")
                  )}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export function PopupAssigned({
  AssignedUsers = [],
  client,
  haslead = false,
  apiLoadonSuccess,
}) {
  const T = {
    bg: "#EDE8E1",
    sidebar: "#1a1730",
    sidebarHover: "#252240",
    sidebarActive: "#302d4a",
    card: "#FFFFFF",
    cardBorder: "rgba(0,0,0,0.08)",
    text: "#1a1520",
    textSecondary: "#5c5466",
    textMuted: "#908892",
    accent: "#6366f1",
    accentLight: "rgba(99,102,241,0.08)",
    accentText: "#5457e5",
    shadow: "0 1px 3px rgba(0,0,0,0.06), 0 1px 2px rgba(0,0,0,0.04)",
    shadowMd: "0 2px 8px rgba(0,0,0,0.06), 0 1px 3px rgba(0,0,0,0.04)",
    shadowLg: "0 8px 30px rgba(0,0,0,0.08)",
    inputBg: "#F3EFE9",
    inputBorder: "rgba(0,0,0,0.1)",
  };

  const getInitials = (name = "") => {
    return name
      .trim()
      .split(/\s+/)
      .map((word) => word[0])
      .join("")
      .toUpperCase();
  };

  const [loadingUsers, setLoadingUsers] = useState(false);
  const [productionUsers, setProductionUsers] = useState([]);
  const [showDropdown, setShowDropdown] = useState(false);
  const [search, setSearch] = useState("");
  const [Menu, showMenu] = useState(false);

  const apiCall = useApiCall();

  const fetchUsers = async () => {
    if (loadingUsers) return;
    setLoadingUsers(true);

    try {
      const res = await apiCall({
        method: "get",
        url: `users/agents-list`,
      });

      console.log("Users API Response ===>", res);
      setProductionUsers(res?.agents || []);
    } catch (err) {
      console.error("Error fetching users", err);
    } finally {
      setLoadingUsers(false);
    }
  };

  const [showAll, setShowAll] = useState(false);

  const users = Array.isArray(AssignedUsers) ? AssignedUsers : [];

  const visibleUsers = showAll ? users : users.slice(0, 2);
  const hiddenCount = users.length - 3;

  const remainingUsers = useMemo(() => {
    if (productionUsers?.length == 0) return [];

    const assignedUserIds = new Set(users?.map((u) => u?.userid));

    return productionUsers?.filter(
      (user) => !assignedUserIds.has(user?.userid),
    );
  }, [users, productionUsers]);

  const filteredUsers = useMemo(() => {
    if (!search.trim()) return remainingUsers;

    return remainingUsers.filter((u) =>
      u.name.toLowerCase().includes(search.toLowerCase()),
    );
  }, [search, remainingUsers]);

  const addAssigned = async (user) => {
    const result = await Swal.fire({
      title: "Are you sure?",
      text: "Do you want to assigned this agent?",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Yes",
      cancelButtonText: "Cancel",
    });

    if (!result.isConfirmed) {
      console.log("User cancelled");
      return;
    }

    let data = {
      ...(haslead ? { lead_id: client } : { client_id: client }),
      agent_id: user?.userid,
    };

    try {
      const url = `${haslead ? 'leads/assign-agent-to-lead' : 'users/assign-agent-to-client'}`;

      const apiRes = await apiCall({
        method: "post",
        url,
        data,
      });

      Swal.fire({
        icon: "success",
        title: apiRes?.message || "Assigned Agent Successfully",
        timer: 1500,
      });

      setShowDropdown(false);
      apiLoadonSuccess();
    } catch (error) {
      Swal.fire({
        icon: "error",
        title: "Oops...",
        text:
          error?.response?.data?.respMsg ||
          error?.response?.data?.responseMessage ||
          error?.response?.data?.message ||
          "Something went wrong",
      });
      setShowDropdown(false);
    }
  };

  const deleteAssigned = async (user) => {
    const result = await Swal.fire({
      title: "Are you sure?",
      text: "Do you want to remove this agent into assigned?",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Yes",
      cancelButtonText: "Cancel",
    });

    if (!result.isConfirmed) {
      console.log("User cancelled");
      return;
    }

    let data = {
      ...(haslead ? { lead_id: client } : { client_id: client }),
      agent_id: user?.userid,
      type: `${haslead ? "lead" : "contact"}`,
    };

    try {
      const url = `users/un-assign-agent-to-client`;

      const apiRes = await apiCall({
        method: "post",
        url,
        data,
      });

      Swal.fire({
        icon: "success",
        title: apiRes?.message || "UnAssigned Agent Successfully",
        timer: 1500,
      });

      setShowDropdown(false);
      setShowAll(false);
      apiLoadonSuccess();
    } catch (error) {
      Swal.fire({
        icon: "error",
        title: "Oops...",
        text:
          error?.response?.data?.respMsg ||
          error?.response?.data?.responseMessage ||
          error?.response?.data?.message ||
          "Something went wrong",
      });
      setShowDropdown(false);
    }
  };

  const menuRef = useRef(null);

  useClickOutside(menuRef, () => {
    showMenu(false);
  });

  const assignedRef = useRef(null);

  useClickOutside(assignedRef, () => {
    setShowAll(false);
  });

  const assignedDropdownRef = useRef(null);

  useClickOutside(assignedDropdownRef, () => {
    setShowDropdown(false);
  });

  useEffect(() => {
    fetchUsers();
  }, []);

  return (
    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
      <span style={{ fontSize: 12, color: T.textMuted }}>Assigned</span>
      <div className="flex  gap-1 items-center">
        <div className="relative " ref={assignedRef}>
          <div
            className="flex -space-x-2 cursor-pointer"
            onClick={() => {
              setShowAll(!showAll);
              setShowDropdown(false);
            }}
          >
            {AssignedUsers?.slice(0, 3).map((user) => (
              <>
                <div
                  style={{
                    width: 34,
                    height: 34,
                    borderRadius: 100,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    background: "#6366f1" + "18",
                    color: "#6366f1",
                    fontSize: 12,
                    fontWeight: 700,
                    border: `2px solid #6366f130`,
                  }}
                >
                  {getInitials(user?.name)}
                </div>
                {/* <Avatar
                                                src={user?.userimg}
                                                title={user?.name}
                                                width="34px"
                                                height="34px"
                                                className="border-2 !border-[#6366f130] !text-xs !font-bold !bg-[#6366f118] text-[#6366f1] shadow-sm "
                                              /> */}
              </>
            ))}

            {hiddenCount > 0 && (
              <div className="h-8 w-8 flex items-center justify-center rounded-full bg-[#0057FB] text-white text-xs font-medium border-2 border-white shadow-sm">
                +{hiddenCount}
              </div>
            )}
          </div>

          {showAll && (
            <div className="absolute top-9 left-0 w-60 border border-[#f6f6f6]  bg-white rounded-xl shadow-xl p-3 z-999999 overflow-hidden z-20">
              <div className="flex justify-between items-center mb-2  p-1 border-gray-400 border-b">
                {/* <h4 className="text-sm font-medium">Assigned Agents</h4> */}
                <button
                  onClick={() => setShowAll(false)}
                  className="p-1 rounded hover:bg-gray-100 transition"
                >
                  <X className="w-4 h-4 text-gray-500 hover:text-black" />
                </button>
              </div>

              <div className="!overflow-y-auto w-full max-h-52  h-full">
                {AssignedUsers?.map((each, idx) => (
                  <div
                    key={idx}
                    className="flex items-center justify-between gap-2 p-1 hover:bg-slate-100 transition-all duration-300"
                  >
                    <AvatarProfile
                      src={each?.userimg}
                      title={each?.name}
                      className="text-sm"
                    />

                    <span className="flex justify-end">
                      <Tooltip
                        key={`${idx}-unassigned`}
                        className={`${Style.Tooltip}`}
                        boxClassName={Style.TooltipBox}
                        content="Delete"
                        position={"bottom center"}
                      >
                        <div
                          onClick={() => {
                            deleteAssigned(each);
                          }}
                          className="p-2 text-red-600"
                        >
                          <Trash2 size={18} />
                        </div>
                      </Tooltip>
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        <div>
          <div
            className="projectDetail-cardLeftUserAddBtn selectUser relative"
            ref={assignedDropdownRef}
          >
            <button
              className="bg-[#0057FB] h-8 w-8 text-white flex justify-center text-[12px] p-1 rounded-full items-center gap-1 selectUser-btn cursor-pointer"
              onClick={(e) => {
                e.stopPropagation();
                setShowDropdown(!showDropdown);
                setShowAll(false);
              }}
            >
              {" "}
              <Plus className="h-5 w-5" />
            </button>

            {showDropdown && (
              <div className="selectUser-view customSearch mt-2 border border-[#f6f6f6] shadow-xl p-3 rounded-xl  bg-white w-64 absolute left-0  z-20">
                <div className="selectUser-search flex items-center gap-2  mb-2 border-gray-400 border-b pb-1">
                  <div className="icon">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="18"
                      height="18"
                      viewBox="0 0 18 18"
                      fill="none"
                    >
                      <mask
                        id="mask1"
                        style={{ maskType: "alpha" }}
                        maskUnits="userSpaceOnUse"
                        x="0"
                        y="0"
                        width="18"
                        height="18"
                      >
                        <rect width="18" height="18" fill="#D9D9D9"></rect>
                      </mask>
                      <g mask="url(#mask1)">
                        <path
                          d="M19.6 21L13.3 14.7C12.8 15.1 12.225 15.4167 11.575 15.65C10.925 15.8833 10.2333 16 9.5 16C7.68333 16 6.14583 15.3708 4.8875 14.1125C3.62917 12.8542 3 11.3167 3 9.5C3 7.68333 3.62917 6.14583 4.8875 4.8875C6.14583 3.62917 7.68333 3 9.5 3C11.3167 3 12.8542 3.62917 14.1125 4.8875C15.3708 6.14583 16 7.68333 16 9.5C16 10.2333 15.8833 10.925 15.65 11.575C15.4167 12.225 15.1 12.8 14.7 13.3L21 19.6L19.6 21ZM9.5 14C10.75 14 11.8125 13.5625 12.6875 12.6875C13.5625 11.8125 14 10.75 14 9.5C14 8.25 13.5625 7.1875 12.6875 6.3125C11.8125 5.4375 10.75 5 9.5 5C8.25 5 7.1875 5.4375 6.3125 6.3125C5.4375 7.1875 5 8.25 5 9.5C5 10.75 5.4375 11.8125 6.3125 12.6875C7.1875 13.5625 8.25 14 9.5 14Z"
                          fill="#999999"
                        ></path>
                      </g>
                    </svg>
                  </div>
                  <input
                    type="text"
                    placeholder="Search Agent..."
                    className="customSearchInput border rounded px-2 py-1 w-full"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                  />
                </div>

                <div className="selectUser-list max-h-48 overflow-y-auto">
                  <ul className="customSearchList ProjectAssignUsersList">
                    {filteredUsers?.map((user) => (
                      <li
                        key={user?.userid}
                        className="UserForAssign flex items-center gap-2 p-1 cursor-pointer hover:bg-gray-100 rounded"
                        onClick={() => {
                          addAssigned(user);
                        }}
                      >
                        <AvatarProfile
                          src={user?.userimg}
                          title={user?.name}
                          className="text-sm"
                        />
                      </li>
                    ))}
                    {loadingUsers && (
                      <li className="p-2 text-center text-gray-400 text-sm">
                        Loading...
                      </li>
                    )}
                    {filteredUsers?.length == 0 && (
                      <li className="p-1 text-gray-400">No users found</li>
                    )}
                  </ul>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
